import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { api, apiError } from "@/lib/api";
import { SPACE_COLORS, SPACE_ICONS } from "@/lib/constants";
import { ENVIRONMENTS, ENV_ORDER, envKeyOf, tint } from "@/lib/environments";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";

const TIMEZONES = ["Europe/Madrid", "Europe/London", "America/Mexico_City", "America/Bogota", "America/Buenos_Aires", "America/New_York", "UTC"];

export const GeneralTab = ({ ws, members }) => {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [name, setName] = useState(ws.name);
  const [description, setDescription] = useState(ws.description || "");
  const [icon, setIcon] = useState(ws.icon);
  const [color, setColor] = useState(ws.color);
  const [tz, setTz] = useState(ws.timezone || "Europe/Madrid");
  const [environment, setEnvironment] = useState(envKeyOf(ws));
  const [saving, setSaving] = useState(false);
  const [confirmName, setConfirmName] = useState("");
  const [transferTo, setTransferTo] = useState("");

  useEffect(() => {
    setName(ws.name); setDescription(ws.description || ""); setIcon(ws.icon); setColor(ws.color); setTz(ws.timezone || "Europe/Madrid"); setEnvironment(envKeyOf(ws));
  }, [ws]);

  const isOwner = ws.my_role === "owner";
  const otherMembers = members.filter((m) => m.role !== "owner" && m.status === "active");

  const save = async () => {
    setSaving(true);
    try {
      await api.put(`/workspaces/${ws.id}`, { name, description, icon, color, timezone: tz, environment });
      qc.invalidateQueries({ queryKey: ["workspace", ws.id] });
      qc.invalidateQueries({ queryKey: ["home"] });
      qc.invalidateQueries({ queryKey: ["workspaces"] });
      toast.success("Configuración guardada");
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setSaving(false);
    }
  };

  const transfer = async () => {
    try {
      await api.post(`/workspaces/${ws.id}/transfer`, { new_owner_member_id: transferTo });
      qc.invalidateQueries();
      toast.success("Propiedad transferida");
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const archive = async () => {
    try {
      await api.post(`/workspaces/${ws.id}/archive`);
      qc.invalidateQueries({ queryKey: ["home"] });
      toast.success("Espacio archivado");
      navigate("/");
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const remove = async () => {
    try {
      await api.delete(`/workspaces/${ws.id}`, { data: { confirm_name: confirmName } });
      qc.invalidateQueries({ queryKey: ["home"] });
      toast.success("Espacio eliminado");
      navigate("/");
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  return (
    <div className="space-y-6" data-testid="settings-general-tab">
      <Card className="p-6 space-y-5">
        <div className="space-y-2">
          <Label htmlFor="ws-name">Nombre</Label>
          <Input id="ws-name" data-testid="settings-name-input" value={name} onChange={(e) => setName(e.target.value)} maxLength={100} />
        </div>
        <div className="space-y-2">
          <Label htmlFor="ws-desc">Descripción</Label>
          <Textarea id="ws-desc" data-testid="settings-description-input" value={description}
            onChange={(e) => setDescription(e.target.value)} rows={2} maxLength={500} />
        </div>
        <div className="space-y-2">
          <Label>Icono</Label>
          <div className="flex flex-wrap gap-1.5">
            {SPACE_ICONS.map((ic) => (
              <button key={ic} data-testid={`settings-icon-${ic}`} onClick={() => setIcon(ic)}
                className={`h-9 w-9 rounded-lg text-lg flex items-center justify-center border transition-colors ${
                  icon === ic ? "border-primary bg-accent" : "border-transparent hover:bg-secondary"
                }`}>{ic}</button>
            ))}
          </div>
        </div>
        <div className="space-y-2">
          <Label>Color</Label>
          <div className="flex flex-wrap gap-2">
            {SPACE_COLORS.map((c) => (
              <button key={c} data-testid={`settings-color-${c.replace("#", "")}`} onClick={() => setColor(c)}
                className={`h-8 w-8 rounded-full transition-transform ${color === c ? "ring-2 ring-offset-2 ring-primary scale-110" : ""}`}
                style={{ backgroundColor: c }} />
            ))}
          </div>
        </div>
        <div className="space-y-2">
          <Label>Entorno</Label>
          <div className="flex flex-wrap gap-2">
            {ENV_ORDER.map((key) => {
              const env = ENVIRONMENTS[key];
              const active = environment === key;
              return (
                <button
                  key={key}
                  type="button"
                  data-testid={`settings-env-${key}`}
                  onClick={() => setEnvironment(key)}
                  className="px-3 h-8 rounded-full text-xs font-semibold flex items-center gap-1 transition-colors"
                  style={active
                    ? { backgroundColor: env.color, color: "#FFFFFF" }
                    : { backgroundColor: tint(env.color, 0.14), color: env.text }}
                >
                  <span aria-hidden>{env.emoji}</span> {env.label}
                </button>
              );
            })}
          </div>
        </div>
        <div className="space-y-2">
          <Label>Zona horaria</Label>
          <Select value={tz} onValueChange={setTz}>
            <SelectTrigger data-testid="settings-timezone-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              {TIMEZONES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <Button data-testid="settings-save-btn" onClick={save} disabled={saving}>
          {saving ? "Guardando..." : "Guardar cambios"}
        </Button>
      </Card>

      {isOwner && (
        <Card className="p-6 space-y-6 border-destructive/30" data-testid="settings-danger-zone">
          <h3 className="font-heading font-semibold text-destructive">Zona de peligro</h3>

          <div className="flex flex-col sm:flex-row sm:items-center gap-3 justify-between">
            <div>
              <p className="font-medium text-sm">Transferir propiedad</p>
              <p className="text-xs text-muted-foreground">El nuevo propietario tendrá control total. Tú pasarás a ser administrador.</p>
            </div>
            <div className="flex gap-2">
              <Select value={transferTo} onValueChange={setTransferTo}>
                <SelectTrigger className="w-44" data-testid="transfer-owner-select"><SelectValue placeholder="Elegir miembro" /></SelectTrigger>
                <SelectContent>
                  {otherMembers.map((m) => <SelectItem key={m.id} value={m.id}>{m.name}</SelectItem>)}
                </SelectContent>
              </Select>
              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <Button variant="outline" disabled={!transferTo} data-testid="transfer-owner-btn">Transferir</Button>
                </AlertDialogTrigger>
                <AlertDialogContent>
                  <AlertDialogHeader>
                    <AlertDialogTitle>¿Transferir la propiedad?</AlertDialogTitle>
                    <AlertDialogDescription>Esta acción no se puede deshacer desde tu cuenta.</AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel>Cancelar</AlertDialogCancel>
                    <AlertDialogAction data-testid="transfer-owner-confirm-btn" onClick={transfer}>Transferir</AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            </div>
          </div>

          <div className="flex items-center gap-3 justify-between">
            <div>
              <p className="font-medium text-sm">Archivar Espacio</p>
              <p className="text-xs text-muted-foreground">El Espacio dejará de estar visible, pero sus datos se conservan.</p>
            </div>
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button variant="outline" data-testid="archive-space-btn">Archivar</Button>
              </AlertDialogTrigger>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>¿Archivar este Espacio?</AlertDialogTitle>
                  <AlertDialogDescription>Dejará de aparecer para todos los miembros.</AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel>Cancelar</AlertDialogCancel>
                  <AlertDialogAction data-testid="archive-space-confirm-btn" onClick={archive}>Archivar</AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          </div>

          <div className="space-y-3">
            <div>
              <p className="font-medium text-sm">Eliminar Espacio</p>
              <p className="text-xs text-muted-foreground">
                Eliminación permanente. Escribe <span className="font-semibold">{ws.name}</span> para confirmar.
              </p>
            </div>
            <div className="flex gap-2">
              <Input data-testid="delete-space-confirm-input" value={confirmName}
                onChange={(e) => setConfirmName(e.target.value)} placeholder={ws.name} className="max-w-xs" />
              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <Button variant="destructive" disabled={confirmName !== ws.name} data-testid="delete-space-btn">Eliminar</Button>
                </AlertDialogTrigger>
                <AlertDialogContent>
                  <AlertDialogHeader>
                    <AlertDialogTitle>¿Eliminar definitivamente?</AlertDialogTitle>
                    <AlertDialogDescription>Se eliminarán los miembros, equipos e invitaciones de {ws.name}. Esta acción es irreversible.</AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel>Cancelar</AlertDialogCancel>
                    <AlertDialogAction data-testid="delete-space-confirm-btn" onClick={remove}
                      className="bg-destructive text-destructive-foreground hover:bg-destructive/90">
                      Eliminar Espacio
                    </AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            </div>
          </div>
        </Card>
      )}
    </div>
  );
};
